import { useEffect, useRef } from "react";
import { gsap, EASE } from "./gsap";
import { prefersReducedMotion } from "./prefersReducedMotion";

interface EntranceOptions {
  selector?: string;
  stagger?: number;
  distance?: number;
  duration?: number;
  delay?: number;
  deps?: unknown[];
}

/**
 * On-mount entrance reveal (NOT scroll-triggered) for above-the-fold content
 * such as the hero, which is already in view on load. Children/selector matches
 * rise and fade in with a stagger. No-op under `prefers-reduced-motion`.
 */
export function useEntrance<T extends HTMLElement = HTMLDivElement>(
  options: EntranceOptions = {}
) {
  const containerRef = useRef<T>(null);
  const {
    selector,
    stagger = 0.14,
    distance = 40,
    duration = 1,
    delay = 0.15,
    deps = [],
  } = options;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const targets = selector
      ? Array.from(container.querySelectorAll<HTMLElement>(selector))
      : (Array.from(container.children) as HTMLElement[]);
    if (targets.length === 0) return;
    if (prefersReducedMotion()) return;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        targets,
        { autoAlpha: 0, y: distance },
        { autoAlpha: 1, y: 0, duration, delay, ease: EASE, stagger }
      );
    }, container);

    return () => ctx.revert();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return containerRef;
}
